<template>
  <div id="app-root">
    <!-- Boot / Loading global -->
    <div id="boot" class="boot-overlay hidden" :class="{ hidden: ocultarBoot }">
      <div class="boot-spinner"></div>
      <div class="boot-text">Cargando…</div>
    </div>

    <!-- Layout principal -->
    <layout-admin v-if="shouldRenderLayout">
      <router-view />
    </layout-admin>
    
    <!-- Para Welcome y otras páginas sin layout - solo mostrar cuando proyectos estén listos -->
    <router-view v-else-if="shouldRenderPublicView" />
  </div>
</template>

<script>
import { getUserFromToken, setupCrossTabSync, earlyHydration, performLogout } from './utils/auth';
import { clearNotificationState, fetchWithToken, safeJsonParse } from './utils/helpers';
import { applyProjectLocale } from './utils/localeBootstrap';
import { cargarConstantesAgronomicas } from './services/agronomia';
import LayoutAdmin from './components/Layout.vue';

// `/recuperar-clave` es publica: sin esta marca App.vue no la renderiza —no entra
// por layout (no hay sesion) ni por vista publica— y se ve la pantalla en blanco.
const PUBLIC_PATHS = ['/login', '/welcome', '/no-found', '/no-authorization', '/recuperar-clave'];

function getCurrentPath(routePath = '') {
  return String(routePath || window.location.pathname || '').toLowerCase();
}

export default {
  name: 'App',
  components: {
    LayoutAdmin
  },
  setup() {
    earlyHydration();
    const removeStorageListener = setupCrossTabSync();
    return { removeStorageListener };
  },
  data() {
    const currentPath = getCurrentPath(this.$route?.path);
    const isPublicPath = PUBLIC_PATHS.includes(currentPath);
    return {
      menuIsReady: false,
      proyectosListos: isPublicPath,
      proyectoSelectedValue: '',
      isCollapsed: false,
      showDropdown: false,
      showDropdownNotifications: false
    };
  },
  computed: {
    isPublicRoute() {
      return PUBLIC_PATHS.includes(getCurrentPath(this.$route?.path));
    },
    hasActiveSession() {
      return !!sessionStorage.getItem('token');
    },
    // Rutas CON layout que pueden abrirse SIN proyecto seleccionado. Sin esta
    // excepción no renderizan ni el layout (les falta proyecto) ni la vista
    // pública (no están en PUBLIC_PATHS), y se ve una pantalla en blanco.
    //
    //   /asistente  Configuración Inicial: es donde se crea el primer proyecto.
    //   /mi-cuenta  a donde el guard manda a quien tiene la clave puesta por un
    //               tercero. Sin layout se quedaba sin menú de cuenta, o sea sin
    //               botón para salir, justo en la única pantalla que se le deja.
    isRutaSinProyecto() {
      return ['/asistente', '/mi-cuenta'].includes(getCurrentPath(this.$route?.path));
    },
    shouldRenderLayout() {
      if (!this.hasActiveSession || this.isPublicRoute) return false;
      if (this.isRutaSinProyecto) return true;
      return this.menuIsReady && !!this.proyectoSelectedValue;
    },
    shouldRenderPublicView() {
      return this.isPublicRoute || this.proyectosListos;
    },
    ocultarBoot() {
      return this.shouldRenderLayout || this.shouldRenderPublicView;
    }
  },
  watch: {
    '$route.fullPath'() {
      this.syncSessionState();
    },
    proyectosListos(nuevoValor) {
      if (nuevoValor) {
        console.log('App.vue - Proyectos listos, ocultando boot screen');
        window.hideBoot?.();
        const bootEl = document.getElementById('boot');
        if (bootEl) {
          bootEl.classList.add('hidden');
        }
      }
    }
  },
  async mounted() {
    this.syncSessionState();
    window.addEventListener('hb:session-updated', this.syncSessionState);

    if (this.isPublicRoute) {
      this.proyectosListos = true;
      window.hideBoot?.();
    }

    const usuarioActual = getUserFromToken();
    this.$root.$data.usuarioActual = usuarioActual;

    if (!usuarioActual) {
      this.proyectosListos = true;
      return;
    }

    if (this.isPublicRoute) {
      return;
    }

    const menuGuardado = sessionStorage.getItem('menu');
    const proyectoSeleccionado = sessionStorage.getItem('proyectSelected');
    const proyectosGuardados = sessionStorage.getItem('proyectos');

    // Si hay menu y proyecto, cargar todo
    if (menuGuardado && proyectoSeleccionado) {
      const menu = safeJsonParse(menuGuardado, null);
      if (menu) {
        this.loadMenu(menu);
        this.menuIsReady = true;
      }
      
      // Proyectos listos
      this.proyectosListos = true;
    } else if (usuarioActual && !proyectosGuardados) {
      // Si no hay proyectos guardados, intentar cargarlos del API
      console.log('App.vue - Cargando proyectos del API para usuario:', usuarioActual.id);
      try {
        const res = await fetchWithToken(`/api/general-access-user?id=${usuarioActual.id}`, {
          method: 'GET'
        });

        console.log('App.vue - Respuesta del API:', res.status);
        
        if (res.ok) {
          const data = await res.json();
          console.log('App.vue - Data recibida del API:', data);
          
          let proyectos = [];
          
          // Intentar diferentes estructuras de respuesta
          if (data.status === 200 && data.data) {
            proyectos = data.data;
          } else if (data.data && Array.isArray(data.data)) {
            proyectos = data.data;
          } else if (Array.isArray(data)) {
            proyectos = data;
          }
          
          if (proyectos.length > 0) {
            console.log('App.vue - Proyectos encontrados:', proyectos.length);
            sessionStorage.setItem('proyectos', JSON.stringify(proyectos));
            localStorage.setItem('hb:proyectos', JSON.stringify(proyectos));
            
            // Si hay un solo proyecto, cargar automáticamente
            if (proyectos.length === 1) {
              const proyecto = proyectos[0];
              sessionStorage.setItem('proyectSelected', proyecto.id);
              sessionStorage.setItem('menu', JSON.stringify(proyecto.menu?.[0] || {}));
              sessionStorage.setItem('proyectSelectedName', proyecto.proyectoName);

              // Este camino cambia el proyecto activo DESPUÉS de que corrió el
              // guard del router y sin disparar 'hb:session-updated', así que
              // el Formato Regional se re-aplica acá explícitamente.
              applyProjectLocale();

              if (proyecto.menu?.[0]) {
                this.loadMenu(proyecto.menu[0]);
                this.menuIsReady = true;
              }
            }
          }
        } else {
          console.error('App.vue - Error en respuesta del API:', res.status);
          try {
            const errorData = await res.json();
            console.error('Error data:', errorData);
          } catch (e) {}
        }
      } catch (e) {
        console.warn('App.vue - Error al cargar proyectos:', e);
      }
      
      // Marcar proyectos como listos incluso si hubo error
      this.proyectosListos = true;
    } else if (proyectosGuardados) {
      // Si ya hay proyectos guardados, marcar como listos
      console.log('App.vue - Proyectos ya estaban guardados');
      this.proyectosListos = true;
    }

    // Ocultar boot screen cuando proyectosListos sea true
    setTimeout(() => {
      if (this.proyectosListos || this.menuIsReady) {
        window.hideBoot?.();
        const bootEl = document.getElementById('boot');
        if (bootEl) {
          bootEl.classList.add('hidden');
        }
      }
    }, 500);

    this.appResizeHandler = () => {
      if (window.innerWidth <= 768) {
        this.isCollapsed = true;
      }
    };

    this.appResizeHandler();
    window.addEventListener('resize', this.appResizeHandler);

    this.appClickHandler = (e) => {
      const bell = document.querySelector('.fa-bell');
      const panel = document.querySelector('.notif-menu');
      if (panel && !panel.contains(e.target) && !bell?.contains(e.target)) {
        this.showDropdownNotifications = false;
      }
    };
    window.addEventListener('click', this.appClickHandler);

    window.hideBoot();
  },
  beforeUnmount() {
    this.removeStorageListener?.();
    window.removeEventListener('hb:session-updated', this.syncSessionState);
    if (this.appResizeHandler) window.removeEventListener('resize', this.appResizeHandler);
    if (this.appClickHandler) window.removeEventListener('click', this.appClickHandler);
  },
  methods: {
    hasValidMenuSelection() {
      const selected = sessionStorage.getItem('proyectSelected');
      const rawMenu = sessionStorage.getItem('menu');
      if (!selected || !rawMenu) return false;
      try {
        const parsed = safeJsonParse(rawMenu, null);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) && Object.keys(parsed).length > 0;
      } catch {
        return false;
      }
    },
    syncSessionState() {
      const token = sessionStorage.getItem('token');
      if (!token) {
        this.proyectoSelectedValue = '';
        this.menuIsReady = false;
        this.proyectosListos = true;
        return;
      }

      this.proyectoSelectedValue = sessionStorage.getItem('proyectSelected') || '';
      this.menuIsReady = this.hasValidMenuSelection();

      // Constantes agronomicas (eficiencias por tipo de riego, texturas,
      // defaults): una sola peticion por sesion, cacheada en services/agronomia.js.
      //
      // Va ACA y no en setup(): setup() corre una sola vez al montar la SPA, y en
      // el camino normal (entrar por /login y autenticarse) todavia no hay token,
      // asi que la peticion no se hacia nunca y la sesion entera se quedaba con
      // los fallbacks. `syncSessionState` corre al montar Y en cada
      // 'hb:session-updated', que es lo que emiten Login, Welcome y el Asistente.
      //
      // No se espera ni se encadena nada: mientras no llegue, la app usa los
      // fallbacks, que son los mismos valores del backend.
      cargarConstantesAgronomicas();
    },
    toggleSidebar() {
      this.isCollapsed = !this.isCollapsed;
      if (this.isCollapsed) {
        this.closeAllDropdowns();
      }
    },
    closeAllDropdowns() {
      this.showDropdown = false;
      this.showDropdownNotifications = false;
    },
    toggleDropdownNotifications() {
      this.showDropdownNotifications = !this.showDropdownNotifications;
    },
    loadMenu(menu) {
      if (!menu) {
        console.warn('No se proporcionó menú para cargar.');
        return;
      }
    },
    logout() {
      clearNotificationState();
      performLogout({ redirectTo: '/login', broadcast: true });
    }
  }
};
</script>

<style scoped>
#app-root {
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.boot-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #f3f4f6;
  display: grid;
  place-items: center;
  z-index: 99999;
}

.boot-overlay.hidden {
  display: none;
}

/* En modo oscuro el overlay de carga debe usar el fondo del tema, no el claro
   fijo (#f3f4f6): antes las transiciones "Cargando…" parpadeaban en blanco. */
html[data-theme="dark"] .boot-overlay {
  background: var(--hb-bg, #0d1310);
}

html[data-theme="dark"] .boot-spinner {
  border-color: var(--hb-surface-3, #212d26);
  border-top-color: #ef4444;
}

html[data-theme="dark"] .boot-text {
  color: var(--hb-text-muted, #9aa7ad);
}

.boot-spinner {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 6px solid #e5e7eb;
  border-top-color: #ef4444;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}


</style>
